// // client/src/hooks/useSocket.ts
// import { io, Socket } from 'socket.io-client';
// import { useEffect, useState, useCallback } from 'react';

// type SocketOptions = {
//   autoConnect?: boolean;
//   reconnectionAttempts?: number;
//   reconnectionDelay?: number;
//   transports?: ('websocket' | 'polling')[];
// };

// const defaultOptions: SocketOptions = {
//   autoConnect: true,
//   reconnectionAttempts: 5,
//   reconnectionDelay: 5000,
//   transports: ['websocket'],
// };

// export const useSocket = (url: any, options: SocketOptions = {}) => {
//   const [socket, setSocket] = useState<Socket | null>(null);
//   const [isConnected, setIsConnected] = useState(false);
//   const [error, setError] = useState<Error | null>(null);

//   const connect = useCallback(() => {
//     if (socket?.connected) return;

//     const socketInstance = io(url, {
//       ...defaultOptions,
//       ...options,
//     });

//     const onConnect = () => {
//       setIsConnected(true);
//       setError(null);
//     };

//     const onDisconnect = () => {
//       setIsConnected(false);
//     };

//     const onConnectError = (err: Error) => {
//       setError(err);
//       setIsConnected(false);
//     };

//     socketInstance.on('connect', onConnect);
//     socketInstance.on('disconnect', onDisconnect);
//     socketInstance.on('connect_error', onConnectError);

//     setSocket(socketInstance);

//     return () => {
//       socketInstance.off('connect', onConnect);
//       socketInstance.off('disconnect', onDisconnect);
//       socketInstance.off('connect_error', onConnectError);
//     };
//   }, [url, options, socket]);

//   const disconnect = useCallback(() => {
//     if (socket) {
//       socket.disconnect();
//       setSocket(null);
//       setIsConnected(false);
//     }
//   }, [socket]);

//   useEffect(() => {
//     connect();

//     return () => {
//       disconnect();
//     };
//   }, []);

//   const emit = useCallback(
//     (event: string, ...args: unknown[]) => {
//       if (socket && isConnected) {
//         socket.emit(event, ...args);
//       } else {
//         console.warn('Socket not connected - message not sent');
//       }
//     },
//     [socket, isConnected]
//   );

//   const on = useCallback(
//     (event: string, callback: (...args: unknown[]) => void) => {
//       if (socket) {
//         socket.on(event, callback);
//       }

//       return () => {
//         if (socket) {
//           socket.off(event, callback);
//         }
//       };
//     },
//     [socket]
//   );

//   return {
//     socket,
//     isConnected,
//     error,
//     connect,
//     disconnect,
//     emit,
//     on,
//   };
// };














// client/src/context/SocketContext.tsx
import { createContext, useContext, useEffect, useState, useCallback, ReactNode, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';

type SocketOptions = {
  autoConnect?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  transports?: ('websocket' | 'polling')[];
};

type SocketContextType = {
  socket: Socket | null;
  isConnected: boolean;
  error: Error | null;
  connect: () => void;
  disconnect: () => void;
  emit: (event: string, ...args: unknown[]) => void;
  on: (event: string, callback: (...args: unknown[]) => void) => () => void;
};

const SocketContext = createContext<SocketContextType | undefined>(undefined);

const defaultOptions: SocketOptions = {
  autoConnect: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 5000,
  transports: ['websocket'],
};

type SocketProviderProps = {
  url: string|undefined;
  options?: SocketOptions;
  children: ReactNode;
  onError?: (error: Error) => void;
};

export function SocketProvider({ url, options = {}, children }: any) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Memoize options to prevent unnecessary recreations
  const socketOptions = useMemo(() => ({
    ...defaultOptions,
    ...options,
  }), [JSON.stringify(options)]); // Stringify to deep compare

  const connect = useCallback(() => {
    if (socket?.connected) {
      return;
    }

    // Disconnect existing socket if any
    if (socket) {
      socket.disconnect();
    }

    if (!url) {
      console.warn('Socket URL is not defined');
      return;
    }

    // Get auth token from localStorage
    const authToken = localStorage.getItem('authToken');
    
    // Add auth token to socket connection options
    const optionsWithAuth = {
      ...socketOptions,
      auth: authToken ? { token: authToken } : undefined,
      query: authToken ? { token: authToken } : undefined,
      extraHeaders: authToken ? { 'X-Auth-Token': authToken } : undefined,
    };

    const socketInstance = io(url, optionsWithAuth);

    const onConnect = () => {
      setIsConnected(true);
      setError(null);
      console.log('Socket connected');
    };

    const onDisconnect = (reason: string) => {
      setIsConnected(false);
      console.log('Socket disconnected:', reason);
    };

    const onConnectError = (err: Error) => {
      setError(err);
      setIsConnected(false);
      console.error('Socket connection error:', err);
    };

    socketInstance.on('connect', onConnect);
    socketInstance.on('disconnect', onDisconnect);
    socketInstance.on('connect_error', onConnectError);

    setSocket(socketInstance);
  }, [url, socketOptions]);

  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket]);

  // Auto-connect on mount
  useEffect(() => {
    if (url) {
      connect();
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [url]); // Only reconnect if URL changes

  const handleVisibilityChange = useCallback(() => {
    if (socket?.connected && document.visibilityState === 'hidden') {
      // Optionally disconnect when tab is hidden
      // socket.disconnect();
    }
  }, [socket]);
  
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);



  const emit = useCallback(
    (event: string, ...args: unknown[]) => {
      if (socket && isConnected) {
        socket.emit(event, ...args);
      } else {
        console.warn(`Socket not connected - message "${event}" not sent`, { socket: !!socket, isConnected });
      }
    },
    [socket, isConnected]
  );
    
      const on = useCallback(
        (event: string, callback: (...args: unknown[]) => void) => {
          if (socket) {
            socket.on(event, callback);
          }
    
          return () => {
            if (socket) {
              socket.off(event, callback);
            }
          };
        },
        [socket]
      );

  const value :any= {
    socket,
    isConnected,
    error,
    connect,
    disconnect,
    emit,
    on,
  };

  return <SocketContext.Provider value={value} >
      {children}
    </SocketContext.Provider >
  
}

export function useSocketContext() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocketContext must be used within a SocketProvider');
  }
  return context;
}